import { orange } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

export interface IColorsBase {
  greenOriginal: string;
  white: string;
  blue: string;
  blueLight: string;
  blueDark: string;
  green: string;
  greenLight: string;
  greenLightest: string;
  greenDark: string;
  grey: string;
  greyLight: string;
  greyDark: string;
  greyBorder: string;
  greenAlt: string;
  validateFail: string;
  validateOk: string;
  red: string;
  redLight: string;
  redDark: string;
  black: string;
  blackLight: string;
  blackDark: string;
  coolBlue: string;
  coolBlueLight: string;
  coolBlueDark: string;
  greyBackground: string;
  greyDivider: string;
  greenBar: string;
  greenSuperLight: string;
  greyPlaceholder: string;
}

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      danger: string;
    };
    colors: IColorsBase;
  }

  interface ThemeOptions {
    status?: {
      danger?: string;
    };
    colors: IColorsBase;
  }
}

interface IColors extends IColorsBase {
  greenLightest: string;
  orange: string;
  orangeDark: string;
  orangeLight: string;
}

export const colors: IColors = {
  coolBlue: "#0076CF",
  coolBlueLight: "#CCE4f6",
  coolBlueDark: "#004B9D",

  blue: "#141B4D",
  blueLight: "#424279",
  blueDark: "#000026",

  green: "#99F2E4",
  greenLight: "#CCF9F2",
  greenLightest: "#E6FCF9",
  greenDark: "#00B19D",

  greenBar: "#ADF0E4",
  greenOriginal: "#33E4C9",
  greenSuperLight: "#C8FFF4",

  greenAlt: "#1BD114",
  validateFail: "#E40028",
  validateOk: "#1BD114",

  orange: "#FF5900",
  orangeDark: "#C44500",
  orangeLight: "#FFCDB2",

  red: "#F82323",
  redLight: "#FF9191",
  redDark: "#D61313",

  white: "#FFFFFF",

  grey: "#7F8481",
  greyLight: "#AEB4B0",
  greyDark: "#535755",
  greyBorder: "#808381",
  greyBackground: "#F3F3F3",
  greyDivider: "#808381",
  greyPlaceholder: "#0000001A",

  black: "#000000",
  blackLight: "#2C2C2C",
  blackDark: "#000000",
};

const baseTheme = createTheme();

const stickeritTheme = createTheme({
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          letterSpacing: 0.8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "9px",
          letterSpacing: 0.8,
        },
      },
    },
  },
  typography: {
    fontFamily: ["Poppins"].join(","),
    h1: {
      color: colors.blue,
      fontSize: "32px", // 2rem
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "40px", // 2.5rem
      },
      fontWeight: "400", // Regular
      letterSpacing: "-0.4px", // -0.025rem
    },
    h2: {
      color: colors.blue,
      fontSize: "32px", // 2rem
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "32px", // 2rem
      },
      fontWeight: "400", // Regular
      letterSpacing: "-0.02rem", // -0.32px
    },
    h3: {
      color: colors.blue,
      fontSize: "24px", // 1.5rem
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "28px", // 1.75rem
      },
      fontWeight: "400", // Regular
      letterSpacing: "-0.28px", // -0.0175rem
    },
    h4: {
      color: colors.blue,
      fontSize: "22px", // 1.375rem
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "26px", // 1.625rem
      },
      fontWeight: "400", // Regular
      letterSpacing: "-0.26px", // -0.01375rem
    },
    h5: {
      color: colors.blue,
      fontSize: "20px", // 1.25rem
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "21px", //
      },
      fontWeight: "400", // Regular
    },
    h6: {
      color: colors.blue,
      fontSize: "18px", // 1.125rem
      fontWeight: "400", // Regular
      letterSpacing: "-0.18px", // -0.01125rem
    },
    subtitle1: {
      color: colors.blue,
      fontSize: "16px", // 1rem
      fontWeight: "400", // Regular
      letterSpacing: "-0.16px", // -0.01rem
    },
    subtitle2: {
      color: colors.blue,
      fontSize: "14px", // 14px
      fontWeight: "500", // Medium
      letterSpacing: "-0.14px", // -0.00875rem
    },
    body1: {
      color: colors.blue,
      fontSize: "16px", // 1rem
      fontWeight: "300", // Light
      letterSpacing: "-0.16px", // -0.01rem
    },
    body2: {
      color: colors.blue,
      fontSize: "14px", //
      fontWeight: "300", // Light
      letterSpacing: "-0.14px", // -0.00875rem
    },
    button: {
      color: colors.white,
      fontSize: "14px", // 14px
      fontWeight: "400", // Regular
      letterSpacing: "-0.8px", // -0.05rem
    },
    caption: {
      color: colors.blue,
      fontSize: "12px", // 0.75rem
      fontWeight: "300", // Light
      letterSpacing: "-0.12px", // -0.0075rem
    },
    overline: {
      color: colors.blue,
      fontSize: "10px", // 0.625rem
      fontWeight: "400", // Regular
      letterSpacing: "-0.1px", // -0.00625rem
      textTransform: "capitalize",
    },
  },
  palette: {
    primary: {
      main: colors.coolBlue,
      light: colors.coolBlueLight,
      dark: colors.coolBlueDark,
    },
    secondary: {
      main: colors.green,
      light: colors.greenLight,
      dark: colors.greenDark,
    },
  },
  status: {
    danger: orange[500],
  },
  colors,
});

const theme = createTheme(stickeritTheme, {
  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: {
          background: stickeritTheme.colors.greenDark,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: stickeritTheme.colors.greenDark,
          },
        },
      },
    },
  },
  status: {
    danger: orange[500],
  },
});

export default theme;
